import React, { useState, useEffect } from 'react';
import {
    Box,
    Flex,
    Heading,
    Text,
    VStack,
    HStack,
    Button,
    Input,
    InputGroup,
    InputLeftElement,
    Tag,
    Spinner,
    Grid,
    GridItem,
    IconButton,
    useToast,
    Divider,
    Switch,
    FormControl,
    FormLabel
} from '@chakra-ui/react';
import { LuSearch, LuX, LuPlus, LuGlobe, LuSave } from 'react-icons/lu';
import axios from 'axios';

const AllowedCountriesManagement = ({ customer, onCountriesChange }) => {
    const [allCountries, setAllCountries] = useState([]);
    const [allowedCountries, setAllowedCountries] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [hasChanges, setHasChanges] = useState(false);
    const [saveLoading, setSaveloading] = useState(false);
    const [allowAllCountries, setAllowAllCountries] = useState(false);
    const toast = useToast();


    const getApiHeaders = () => ({

        "x-access-token": localStorage.getItem('user'),
        'Content-Type': 'application/json'
    });


    const fetchAllCountries = async () => {
        try {
            const url = `${process.env.REACT_APP_API_URL}/country/list`;
            const response = await fetch(url, {
                method: 'GET',
                headers: getApiHeaders()
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();



            return data.rows || data || [];
        } catch (error) {
            console.error('Error fetching countries:', error);
            throw error;
        }
    };


    useEffect(() => {
        loadData();
    }, [customer]);


    useEffect(() => {
        if (allowedCountries.length > 0 && !loading) {
            const currentCountryIds = allowedCountries.map(c => c.id);
            onCountriesChange && onCountriesChange(currentCountryIds);
        }
    }, [allowedCountries, onCountriesChange, loading]);

    const loadData = async () => {
        setLoading(true);
        try {

            const countries = await fetchAllCountries();
            setAllCountries(countries);


            let allowedCountryIds = [];

            // Check if "all" countries are allowed
            if (customer?.allow_country === 'all') {
                setAllowAllCountries(true);
                setAllowedCountries([]);
                setHasChanges(false);
                return;
            } else {
                setAllowAllCountries(false);
            }

            if (customer?.allow_country) {
                if (typeof customer.allow_country === 'string') {

                    allowedCountryIds = customer.allow_country.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
                } else if (Array.isArray(customer.allow_country)) {

                    allowedCountryIds = customer.allow_country.map(item => {
                        if (typeof item === 'object' && item.country_id) {
                            return parseInt(item.country_id);
                        } else if (typeof item === 'number') {
                            return item;
                        } else if (typeof item === 'string') {
                            return parseInt(item);
                        }
                        return null;
                    }).filter(id => id !== null && !isNaN(id));
                }
            }




            const allowedCountriesObjects = countries.filter(country =>
                allowedCountryIds.includes(country.id)
            );

            console.log('Allowed countries objects:', allowedCountriesObjects);
            setAllowedCountries(allowedCountriesObjects);
            setHasChanges(false);

        } catch (error) {
            console.error('Error loading data:', error);
            toast({
                title: "Error",
                description: "Failed to load countries data",
                status: "error",
                duration: 5000,
                isClosable: true
            });
        } finally {
            setLoading(false);
        }
    };


    const getAvailableCountries = () => {
        const allowedCountryIds = allowedCountries.map(country => country.id);
        return allCountries
            .filter(country => !allowedCountryIds.includes(country.id))
            .filter(country =>
                searchTerm === '' ||
                country.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                country.code_name.toLowerCase().includes(searchTerm.toLowerCase())
            );
    };


    const addCountry = (country) => {
        const newAllowedCountries = [...allowedCountries, country];
        setAllowedCountries(newAllowedCountries);
        setHasChanges(true);

    };


    const removeCountry = (countryId) => {
        const countryToRemove = allowedCountries.find(c => c.id === countryId);
        const newAllowedCountries = allowedCountries.filter(country => country.id !== countryId);
        setAllowedCountries(newAllowedCountries);
        setHasChanges(true);

        toast({
            title: "Country Removed",
            description: `${countryToRemove?.name} removed from allowed countries. Remember to save your changes.`,
            status: "info",
            duration: 3000,
            isClosable: true
        });
    };


    const resetChanges = () => {
        loadData();
    };

    const handleAllowAllToggle = () => {
        setAllowAllCountries(!allowAllCountries);
        setHasChanges(true);
    };

    const handleSaveChanges = async () => {
        const url = `${process.env.REACT_APP_API_URL}/customer/allow-country`;
        setSaveloading(true)
        const response = await axios.post(url, {
            id: customer.id,
            allow_country: allowAllCountries ? 'all' : allowedCountries?.map((c) => c.id)
        }, {

            headers: getApiHeaders(),

        });
        if (response.status === 200) {
            setSaveloading(false)
            toast({
                title: "Country Updated",
                description: `${response.data.message}`,
                status: "success",
                duration: 3000,
                isClosable: true
            });
        } else {
            setSaveloading(false)
            toast({
                title: "Country Updated",
                description: `${response.data.message}`,
                status: "error",
                duration: 3000,
                isClosable: true
            });
        }

    };



    if (loading) {
        return (
            <Box>
                <Flex justify="center" align="center" py={6}>
                    <VStack spacing={2}>
                        <Spinner size="md" color="blue.500" />
                        <Text color="gray.500" fontSize="sm">Loading countries...</Text>
                    </VStack>
                </Flex>
            </Box>
        );
    }

    return (
        <Box>
            <Box mb={3}>
                <HStack justify="space-between" w="100%" mb={1}>
                    <HStack spacing={2}>
                        <LuGlobe />
                        <Heading size="sm">Allowed Countries Management</Heading>
                    </HStack>

                    {hasChanges && (
                        <HStack spacing={2}>
                            <Button
                                size="xs"
                                isLoading={saveLoading}
                                onClick={handleSaveChanges}
                            >
                                Save
                            </Button>
                            <Button
                                size="xs"
                                variant="outline"
                                onClick={resetChanges}
                            >
                                Reset
                            </Button>
                        </HStack>
                    )}
                </HStack>
                <Text color="gray.600" fontSize="xs">
                    Manage which countries are allowed for {customer?.name || 'this customer'}'s messaging services
                </Text>
            </Box>

            <VStack spacing={3} align="stretch">

                {/* Allow All Countries Switch */}
                <FormControl display="flex" alignItems="center" justifyContent="space-between" p={3} bg="blue.50" borderRadius="md">
                    <FormLabel htmlFor="allow-all-countries" mb="0" fontWeight="medium" fontSize="sm">
                        Allow All Countries
                    </FormLabel>
                    <Switch
                        id="allow-all-countries"
                        isChecked={allowAllCountries}
                        onChange={handleAllowAllToggle}
                        colorScheme="blue"
                    />
                </FormControl>

                <Box opacity={allowAllCountries ? 0.5 : 1} pointerEvents={allowAllCountries ? 'none' : 'auto'}>
                    <Heading size="xs" mb={2}>
                        Currently Allowed Countries ({allowedCountries.length})
                    </Heading>

                    {allowedCountries.length > 0 ? (
                        <Grid templateColumns="repeat(auto-fill, minmax(250px, 1fr))" gap={2}>
                            {allowedCountries.map((country) => (
                                <GridItem key={country.id}>
                                    <Flex
                                        align="center"
                                        justify="space-between"
                                        p={2.5}
                                        border="1px"
                                        borderColor="gray.200"
                                        borderRadius="md"
                                        bg="white"
                                        _hover={{ bg: "gray.50" }}
                                    >
                                        <HStack spacing={2}>
                                            <Tag>
                                                {country.code_name}
                                            </Tag>
                                            <Text fontSize="sm" fontWeight="medium">
                                                {country.name}
                                            </Text>
                                        </HStack>
                                        <IconButton
                                            icon={<LuX />}
                                            size="xs"
                                            variant="ghost"
                                            colorScheme="red"
                                            aria-label={`Remove ${country.name}`}
                                            onClick={() => removeCountry(country.id)}
                                            isDisabled={allowAllCountries}
                                        />
                                    </Flex>
                                </GridItem>
                            ))}
                        </Grid>
                    ) : (
                        <Box
                            p={4}
                            border="1px"
                            borderColor="gray.200"
                            borderRadius="md"
                            bg="gray.50"
                            textAlign="center"
                        >
                            <Text color="gray.500" fontSize="sm">No countries are currently allowed</Text>
                        </Box>
                    )}
                </Box>

                <Divider />

                <Box opacity={allowAllCountries ? 0.5 : 1} pointerEvents={allowAllCountries ? 'none' : 'auto'}>
                    <Heading size="xs" mb={2}>Add New Countries</Heading>

                    <InputGroup mb={3} maxW="400px">
                        <InputLeftElement pointerEvents="none">
                            <LuSearch color="gray.400" />
                        </InputLeftElement>
                        <Input
                            placeholder="Search countries..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            isDisabled={allowAllCountries}
                        />
                    </InputGroup>

                    {getAvailableCountries().length > 0 ? (
                        <Grid templateColumns="repeat(auto-fill, minmax(250px, 1fr))" gap={2}>
                            {getAvailableCountries().slice(0, 12).map((country) => (
                                <GridItem key={country.id}>
                                    <Flex
                                        align="center"
                                        justify="space-between"
                                        p={2.5}
                                        border="1px"
                                        borderColor="gray.200"
                                        borderRadius="md"
                                        bg="white"
                                        _hover={{ bg: "blue.50", borderColor: "blue.300" }}
                                    >
                                        <HStack spacing={2}>
                                            <Tag colorScheme="gray">
                                                {country.code_name}
                                            </Tag>
                                            <Text fontSize="sm" fontWeight="medium">
                                                {country.name}
                                            </Text>
                                        </HStack>
                                        <IconButton
                                            icon={<LuPlus />}
                                            size="xs"
                                            variant="ghost"
                                            colorScheme="blue"
                                            aria-label={`Add ${country.name}`}
                                            onClick={() => addCountry(country)}
                                            isDisabled={allowAllCountries}
                                        />
                                    </Flex>
                                </GridItem>
                            ))}
                        </Grid>
                    ) : (
                        <Box
                            p={4}
                            border="1px"
                            borderColor="gray.200"
                            borderRadius="md"
                            bg="gray.50"
                            textAlign="center"
                        >
                            <Text color="gray.500" fontSize="sm">
                                {searchTerm ? 'No countries found matching your search' : 'All countries are already allowed'}
                            </Text>
                        </Box>
                    )}

                    {searchTerm && getAvailableCountries().length > 12 && (
                        <Text fontSize="xs" color="gray.500" mt={2} textAlign="center">
                            Showing first 12 results. Try a more specific search to find other countries.
                        </Text>
                    )}
                </Box>
            </VStack>
        </Box>
    );
};

export default AllowedCountriesManagement;